import type { PageType } from './plan'

export const CONTENT_CHANNELS = ['linkedin'] as const

export type ContentChannel = (typeof CONTENT_CHANNELS)[number]

export const CONTENT_STATUSES = [
  'draft',
  'pending-handoff',
  'scheduled',
  'published',
  'failed',
] as const

export type ContentStatus = (typeof CONTENT_STATUSES)[number]

/** The shapes a piece of content can take: a short `post` or a long-form `article`. */
export const CONTENT_SHAPES = ['post', 'article'] as const

export type ContentShape = (typeof CONTENT_SHAPES)[number]

/** A `Design`'s renders attached to a post as media; `pageCount` and `renderVersion` are a snapshot at attach time. */
export interface PostDesign {
  designId: string
  pageCount: number
  renderVersion: number
  attachedAt: Date
}

export const SLIDE_ROLES = ['open', 'beat', 'close'] as const

export type SlideRole = (typeof SLIDE_ROLES)[number]

/** One text of a slide. */
export interface SlideComponent {
  type: string
  text: string
}

/** One slide: its role, its block and its texts. */
export interface PostSlide {
  role: SlideRole
  block: string
  components: SlideComponent[]
}

/** The media of a post: its slides in order. */
export interface PostMedia {
  units: PostSlide[]
}

/** The one entity the calendar, content canvas and asset editor all read. */
export interface ContentItem {
  id: string
  shape: ContentShape
  /** Short human title; a post's is the first line of its caption. */
  title: string
  copy: string
  status: ContentStatus
  channels: ContentChannel[]
  /** Exact publish time, or `null` when it has no calendar place yet. */
  scheduledAt: Date | null
  design: PostDesign | null
  /** The article this piece was drawn from, when it is a post promoting one; `null` otherwise. */
  sourceId: string | null
  brandId?: string
  /** The post run that wrote it; `null` for a post made by hand and for an article. */
  runId: string | null
  /** The plan topic it belongs to; `null` for a post made by hand. */
  topicId: string | null
  /** The belief it argues; `null` for a post made by hand and for an article. */
  beliefSlug: string | null
  /** The review score of its run, 0 to 100; `null` until the check ran. */
  score: number | null
  /** Why it needs a person; empty when it is ready. */
  reasons: string[]
  /** Its slides as delivered to the designer; `null` when it has none. */
  media: PostMedia | null
  publishedAt?: Date
  publishedUrl?: string
  publishingError?: string
  currentRevision?: string
  /** When the post document last changed; a failed post's failure time. */
  updatedAt?: Date
}

export const REVISION_AUTHORS = ['raldo', 'user'] as const

export type RevisionAuthor = (typeof REVISION_AUTHORS)[number]

export const REVISION_REASONS = ['generated', 'edited', 'rewritten', 'restored'] as const

export type RevisionReason = (typeof REVISION_REASONS)[number]

/** One finding of a copy rule in code. */
export interface LintFinding {
  rule: string
  severity: 'hard' | 'warning'
  message: string
}

export interface ContentRevision {
  id: string
  postId: string
  /** Id of the revision this one was derived from, or `null` for the first revision. */
  parentId: string | null
  body: string
  author: RevisionAuthor
  reason: RevisionReason
  /** The user instruction that produced this revision, if it was generated or rewritten from one. */
  instruction?: string
  createdAt: Date
}

interface PostResultsBase {
  postId: string
  /** Absent for a hand-written post with no strategy snapshot. */
  intent?: string
  lastSyncedAt: Date
}

/** A post's results before metrics arrive; carries no metric fields, so "not measured" is never read as zero. */
interface PostResultsPending extends PostResultsBase {
  status: 'pending'
}

interface PostResultsMeasured extends PostResultsBase {
  status: 'measured'
  impressions: number
  reach: number
  likes: number
  comments: number
  shares: number
  saves: number
  clicks: number
  views: number
  engagementRate: number
}

/** Discriminated on `status` so a pending read is never mistaken for a measured zero. */
export type PostResults = PostResultsPending | PostResultsMeasured

/** When a post would go out next, read without scheduling; a `null` `publishAt` carries the reason. */
export type NextOpenSlot =
  | { publishAt: string; reason?: undefined }
  | { publishAt: null; reason: 'no_cadence' | 'no_open_slot' }

/** One planned section of the page: its kind in the page type, the heading and the point the writer got. */
export interface ArticlePlanSection {
  id: string
  kind: string
  name: string
  heading: string | null
  point: string | null
  keyFacts: boolean
  cta: boolean
}

/** What the plan stage decided for the page: its names, its definition, its brand sentence and its sections. */
export interface ArticlePlan {
  title: string
  h1: string
  promise: string
  metaDescription: string
  keyword: string | null
  definition: string
  /** The page the definition comes from: this page, or the hub whose definition it reuses. */
  definitionFrom: string
  brandLine: { section: string; place: string; text: string } | null
  sections: ArticlePlanSection[]
  nextStepId: string | null
  topicLinkIds: string[]
  sources: { url: string; by: string }[]
}

/** One section of the article's text with its key facts. */
export interface ArticleSection {
  id: string
  heading: string
  text: string
  keyFacts: string[]
  words: number
}

export const FINDING_CATEGORIES = ['figure', 'company', 'result', 'case', 'source'] as const

export type FindingCategory = (typeof FINDING_CATEGORIES)[number]

/** One sentence the check found unsupported by the material. */
export interface ArticleFinding {
  section: string
  sentence: string
  category: FindingCategory
  why: string
}

/** The reading level of a text: Flesch ease and Flesch-Kincaid grade. */
export interface ReadingLevel {
  words: number
  sentences: number
  wordsPerSentence: number
  longWords: number
  ease: number
  grade: number
}

export const SECTION_TESTS = [
  'statesGain',
  'noNearCopy',
  'noFiller',
  'statesCompany',
  'readable',
  'tableSourced',
] as const

export type SectionTest = (typeof SECTION_TESTS)[number]

/** How one section did on the six tests, and what its words do. */
export interface ArticleSectionCheck {
  id: string
  heading: string
  words: number
  wordsBy: Record<string, number>
  level: ReadingLevel
  tests: Record<SectionTest, boolean>
  tableRowsWithoutSource: string[]
}

/** A sentence that shares a run of words with a ranking page or an AI answer. */
export interface ArticleNearCopy {
  section: string
  sentence: string
  sources: string[]
  shared: string
}

/** The checks of the edited page: the findings, the lint, each section's tests, the shares and the level. */
export interface ArticleChecks {
  findings: ArticleFinding[]
  lint: LintFinding[]
  sections: ArticleSectionCheck[]
  passed: Record<SectionTest, { passed: number; of: number }>
  share: Record<string, number>
  level: ReadingLevel
  nearCopies: ArticleNearCopy[]
}

/** Where one section's content came from, for the reader of the page. */
export interface ArticleSectionTrace {
  id: string
  heading: string
  sources: { by: string; fact: string; url: string | null }[]
  references: { title: string; url: string | null }[]
  influencedBy: { source: string; text: string; url: string | null; how: string }[]
  gains: { kind: string; text: string }[]
  yourAnswers: { ask: string; answer: string }[]
}

export const SLUG_SOURCES = ['existing page', 'keyword', 'question'] as const

export type SlugSource = (typeof SLUG_SOURCES)[number]

/** What the CMS gets: the names, the address, the FAQ schema, the open placeholders and the sources the body cites. */
export interface ArticleHandoff {
  seoTitle: string
  metaDescription: string
  slug: string
  slugFrom: SlugSource
  url: string
  faqSchema: Record<string, unknown> | null
  placeholders: string[]
  sources: { id: string; by: string; url: string }[]
  words: number
}

/** One topic page the article links to, at its current address; `onPage` is whether the live page carried that address at the last check, `null` when it was never checked. */
export interface ArticlePageLink {
  id: string
  question: string
  url: string
  live: boolean
  onPage: boolean | null
}

/** The article's text with every link resolved to a real address, the topic pages it links to, and when the live page was last checked. */
export interface ArticleLinks {
  answer: string
  sections: { id: string; text: string }[]
  body: string
  links: ArticlePageLink[]
  checkedAt: Date | null
}

/** The article written for one planned page; the post of shape `article` carries the schedule and the status. */
export interface ArticleDoc {
  postId: string
  pageId: string
  topicId: string
  type: PageType
  plan: ArticlePlan
  answer: string
  sections: ArticleSection[]
  body: string
  wordCount: number
  /** Absent until the check stage has run. */
  checks: ArticleChecks | null
  /** Absent until the handoff stage has run. */
  trace: ArticleSectionTrace[] | null
  handoff: ArticleHandoff | null
  publishedUrl?: string
  verifiedAt?: Date
  createdAt: Date
  updatedAt: Date
}
